import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { Matomo } from '../helpers/Matomo'
import { PermissionManager } from '../helpers/PermissionManager'
import { useSettings } from '../hooks/useSettings'
import { saveuser } from '../redux/actions/appDataActions'
import { loadAllCustomers } from '../redux/actions/customerActions'
import { contractsApi } from '../redux/api/contractsApi'
import { customersApi, useGetTenantQuery } from '../redux/api/customerApi'
import { documentsApi } from '../redux/api/documentsApi'
import { locationsApi } from '../redux/api/locationsApi'
import { notificationsApi } from '../redux/api/notificationsApi'
import { ordersApi } from '../redux/api/ordersApi'
import { reportsApi } from '../redux/api/reportsApi'
import { useAppDispatch, useAppSelector } from '../redux/store'
import { environment, isDev, mobileVersion } from '../types/constants'
import { ReleaseModal } from './ReleaseNotes/ReleaseModal/ReleaseModal'
import Routes from './Routes'
import { BackendChecker } from './common/BackendChecker/BackendChecker'
import Header from './common/Header/Header'
import Page from './common/Page/Page'
import { VersionChecker } from './common/VersionChecker/VersionChecker'
import Alert from './common/Alert/Alert'

const MAINTENANCE_NOTICE_UNTIL = new Date('2026-08-28T20:00:00+02:00').getTime()

function App() {
  const [sidebarExpanded, setSidebarExpanded] = useState(false)
  const [maintenanceNoticeVisible, setMaintenanceNoticeVisible] = useState(() => Date.now() < MAINTENANCE_NOTICE_UNTIL)
  const { user } = useAuth()
  const currentUser = useAppSelector((state) => state.appData.currentUser)
  const apisReset = useRef(false)
  const location = useLocation()
  const tenant = useGetTenantQuery(user?.tenant || skipToken)
  const dispatch = useAppDispatch()
  const [lang, , langLoading] = useSettings('lang')
  const { t, i18n } = useTranslation('common')
  const containerRelative = !location.pathname.includes('/view=html')
  const newDeltaContractsAllowed = tenant.currentData?.tags.some((t) => t.toLowerCase() === 'deltacontractnew')

  const containerClassName = useMemo(() => {
    let result = `${'environment-' + environment.toString()}`
    result += mobileVersion ? ' contentMobile' : ' content'
    result += user?.isAdmin ? ' is-admin' : ' regular-user'
    result += sidebarExpanded ? ' sidebar-expanded' : ''
    return result
  }, [sidebarExpanded, user?.isAdmin])

  if (apisReset.current === false) {
    apisReset.current = true
    dispatch(customersApi.util.resetApiState())
    dispatch(contractsApi.util.resetApiState())
    dispatch(ordersApi.util.resetApiState())
    dispatch(reportsApi.util.resetApiState())
    dispatch(documentsApi.util.resetApiState())
    dispatch(notificationsApi.util.resetApiState())
    dispatch(locationsApi.util.resetApiState())
  }

  useEffect(() => {
    if (!langLoading) {
      i18n.changeLanguage(lang || 'de')
    }
  }, [i18n, lang, langLoading])

  useEffect(() => {
    if (user?.isInternalUser) {
      document.body.classList.add('grayImg')
    } else {
      document.body.classList.remove('grayImg')
    }
  }, [user?.isInternalUser])

  useEffect(() => {
    dispatch(saveuser(user))
    if (user) {
      dispatch(loadAllCustomers())
    }
    Matomo.init()
    Matomo.setUserId(user?.email ?? user?.displayName ?? 'Unknown')
  }, [dispatch, user])

  return (
    <Page>
      <Header sidebarExpanded={setSidebarExpanded} />
      <div
        id="container"
        style={{ position: containerRelative ? 'relative' : 'static' }}
        className={containerClassName}
      >
        <BackendChecker>
          {currentUser && (
            <Routes
              newDCallowed={newDeltaContractsAllowed}
              userTenant={currentUser?.tenant}
              isAdmin={currentUser?.isAdmin}
              isManager={currentUser?.isManager}
              isInternalUser={currentUser?.isInternalUser}
              canManageOrders={currentUser?.canManageOrders}
              viewCosts={currentUser && PermissionManager.canViewCosts(currentUser)}
            />
          )}
        </BackendChecker>
      </div>
      {!isDev && <VersionChecker />}
      <Alert
        visible={maintenanceNoticeVisible}
        onClose={() => setMaintenanceNoticeVisible(false)}
        title={t('MAINTENANCE_NOTICE_TITLE')}
        message={t('MAINTENANCE_NOTICE_MESSAGE')}
      />
      {<ReleaseModal />}
      {/* <div className='snow'></div> */}
    </Page>
  )
}

export default App
